// Minimal, serializable 3D vector. Core logic stays free of Three.js so it can
// run headlessly (e.g. an authoritative multiplayer server). The render layer
// converts these plain objects to THREE.Vector3 at its boundary.
export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export const v3 = (x = 0, y = 0, z = 0): Vec3 => ({ x, y, z });

export const clone = (a: Vec3): Vec3 => ({ x: a.x, y: a.y, z: a.z });

export const add = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });

export const sub = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });

export const scale = (a: Vec3, s: number): Vec3 => ({ x: a.x * s, y: a.y * s, z: a.z * s });

export const length = (a: Vec3): number => Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);

export const distance = (a: Vec3, b: Vec3): number => length(sub(a, b));

/** Squared distance without allocation — use in hot loops with squared radii. */
export const distanceSq = (a: Vec3, b: Vec3): number => {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  return dx * dx + dy * dy + dz * dz;
};

export const normalize = (a: Vec3): Vec3 => {
  const len = length(a);
  return len > 1e-8 ? scale(a, 1 / len) : v3(0, 0, 0);
};

/** In-place add of `b * s` into `a`. Avoids allocations in hot loops. */
export const addScaledInPlace = (a: Vec3, b: Vec3, s: number): void => {
  a.x += b.x * s;
  a.y += b.y * s;
  a.z += b.z * s;
};
